import { EcologicalZonesAPI } from '@/api/ecologicalZones'

// 缓存配置
const CACHE_CONFIG = {
  STATS_TTL: 5 * 60 * 1000, // 统计数据缓存5分钟
  LIST_TTL: 3 * 60 * 1000,  // 列表数据缓存3分钟
  DETAIL_TTL: 10 * 60 * 1000, // 详情数据缓存10分钟
  MAX_CACHE_SIZE: 100 // 最大缓存条目数
}

// 内存缓存
const cache = {
  stats: { data: null, timestamp: 0 },
  list: new Map(), // key: 查询参数hash, value: { data, timestamp }
  detail: new Map(), // key: zoneId, value: { data, timestamp }
  spatial: new Map() // key: 查询参数hash, value: { data, timestamp }
}

// 缓存工具函数
const cacheUtils = {
  // 生成缓存键
  generateKey(params) {
    return JSON.stringify(params)
  },
  
  // 检查缓存是否有效
  isValid(cacheItem, ttl) {
    return cacheItem && cacheItem.data && (Date.now() - cacheItem.timestamp < ttl)
  },
  
  // 设置缓存
  set(cacheMap, key, data) {
    // 限制缓存大小
    if (cacheMap.size >= CACHE_CONFIG.MAX_CACHE_SIZE) {
      const firstKey = cacheMap.keys().next().value
      cacheMap.delete(firstKey)
    }
    
    cacheMap.set(key, {
      data: JSON.parse(JSON.stringify(data)), // 深拷贝
      timestamp: Date.now()
    })
  },
  
  // 获取缓存
  get(cacheMap, key, ttl) {
    const item = cacheMap.get(key)
    if (this.isValid(item, ttl)) {
      return JSON.parse(JSON.stringify(item.data)) // 深拷贝
    }
    return null
  },
  
  // 清除过期缓存
  clearExpired(cacheMap, ttl) {
    const now = Date.now()
    for (const [key, item] of cacheMap.entries()) {
      if (now - item.timestamp >= ttl) {
        cacheMap.delete(key)
      }
    }
  }
}

// 数据持久化工具
const persistenceUtils = {
  // 保存到localStorage
  save(key, data) {
    try {
      const item = {
        data,
        timestamp: Date.now()
      }
      localStorage.setItem(`ecological_${key}`, JSON.stringify(item))
    } catch (error) {
      // 保存到localStorage失败
    }
  },
  
  // 从localStorage加载
  load(key, ttl = 30 * 60 * 1000) { // 默认30分钟过期
    try {
      const item = JSON.parse(localStorage.getItem(`ecological_${key}`))
      if (item && (Date.now() - item.timestamp < ttl)) {
        return item.data
      }
    } catch (error) {
      // 从localStorage加载失败
    }
    return null
  },
  
  // 清除localStorage数据
  clear(key) {
    try {
      localStorage.removeItem(`ecological_${key}`)
    } catch (error) {
      // 清除localStorage失败
    }
  }
}

const state = {
// 生态管控区统计数据
  stats: null,
  
  // 生态管控区列表数据
  zonesList: [],
  
  // 当前选中的管控区
  selectedZone: null,
  
  // 生态管控区模式是否激活
  isEcologicalMode: false,
  
  // 搜索和筛选条件
  searchQuery: '',
  filters: {
    type: '', // 管控区类型
    level: '', // 管控级别
    city: '' // 所属城市
  },
  
  // 缓存相关
  cache: new Map(),
  cacheStats: {
    hits: 0,
    misses: 0,
    size: 0
  },
  
  // 分页信息
  pagination: {
    page: 1,
    pageSize: 20,
    total: 0
  },
  
  // 地图图层状态
  layerVisible: false,
  layerOpacity: 0.7,
  
  // 空间查询结果
  spatialQueryResults: [],
  
  // 关联的应急资源
  relatedEmergencyResources: [],
  
  // 加载状态
  loading: {
    stats: false,
    list: false,
    detail: false,
    spatial: false,
    export: false
  }
}

const mutations = {
  // 设置统计数据
  SET_STATS(state, stats) {
    state.stats = stats
    // 缓存统计数据
    cache.stats = {
      data: JSON.parse(JSON.stringify(stats)),
      timestamp: Date.now()
    }
    // 持久化保存
    persistenceUtils.save('stats', stats)
  },
  
  // 设置管控区列表
  SET_ZONES_LIST(state, { list, total }) {
    state.zonesList = list || []
    state.pagination.total = total || 0
  },
  
  // 添加管控区到列表
  ADD_ZONES_TO_LIST(state, zones) {
    if (Array.isArray(zones)) {
      state.zonesList.push(...zones)
    }
  },
  
  // 设置选中的管控区
  SET_SELECTED_ZONE(state, zone) {
    state.selectedZone = zone
  },
  
  // 切换生态管控区模式
  SET_ECOLOGICAL_MODE(state, isActive) {
    state.isEcologicalMode = isActive
  },
  
  // 设置搜索查询
  SET_SEARCH_QUERY(state, query) {
    state.searchQuery = query
  },
  
  // 设置筛选条件
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters }
  },
  
  // 重置筛选条件
  RESET_FILTERS(state) {
    state.filters = {
      type: '',
      level: '',
      city: ''
    }
    state.searchQuery = ''
  },
  
  // 设置分页信息
  SET_PAGINATION(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination }
  },
  
  // 设置图层可见性
  SET_LAYER_VISIBLE(state, visible) {
    state.layerVisible = visible
  },
  
  // 设置图层透明度
  SET_LAYER_OPACITY(state, opacity) {
    state.layerOpacity = opacity
  },
  
  // 设置空间查询结果
  SET_SPATIAL_QUERY_RESULTS(state, results) {
    state.spatialQueryResults = results || []
  },
  
  // 设置关联应急资源
  SET_RELATED_EMERGENCY_RESOURCES(state, resources) {
    state.relatedEmergencyResources = resources || []
  },
  
  // 设置加载状态
  SET_LOADING(state, { type, loading }) {
    if (Object.prototype.hasOwnProperty.call(state.loading, type)) {
      state.loading[type] = loading
    }
  },
  
  // 清除所有数据
  CLEAR_ALL_DATA(state) {
    state.zonesList = []
    state.selectedZone = null
    state.spatialQueryResults = []
    state.relatedEmergencyResources = []
    state.pagination = {
      page: 1,
      pageSize: 20,
      total: 0
    }
  }
}

const actions = {
  // 获取统计数据（带缓存）
  async fetchStats({ commit }, { forceRefresh = false } = {}) {
    // 检查缓存
    if (!forceRefresh && cacheUtils.isValid(cache.stats, CACHE_CONFIG.STATS_TTL)) {
      commit('SET_STATS', cache.stats.data)
      return cache.stats.data
    }
    
    // 尝试从localStorage加载
    if (!forceRefresh) {
      const persistedData = persistenceUtils.load('stats', CACHE_CONFIG.STATS_TTL)
      if (persistedData) {
        commit('SET_STATS', persistedData)
        return persistedData
      }
    }
    
    commit('SET_LOADING', { type: 'stats', loading: true })
    try {
      const response = await EcologicalZonesAPI.getStats()
      if (response.success) {
        commit('SET_STATS', response.data)
        return response.data
      }
    } catch (error) {
      console.error('获取生态管控区统计失败:', error)
      // 如果网络请求失败，尝试使用过期的缓存数据
      if (cache.stats.data) {
        // 使用过期的缓存数据
        commit('SET_STATS', cache.stats.data)
        return cache.stats.data
      }
      throw error
    } finally {
      commit('SET_LOADING', { type: 'stats', loading: false })
    }
  },
  
  // 获取管控区列表（带缓存）
  async fetchZonesList({ commit, state }, { reset = false, forceRefresh = false } = {}) {
    const params = {
      keyword: state.searchQuery,
      type: state.filters.type,
      level: state.filters.level,
      city: state.filters.city,
      page: reset ? 1 : state.pagination.page,
      pageSize: state.pagination.pageSize
    }
    
    const cacheKey = cacheUtils.generateKey(params)
    
    // 检查缓存（仅在非强制刷新且为重置请求时）
    if (!forceRefresh && reset) {
      const cachedData = cacheUtils.get(cache.list, cacheKey, CACHE_CONFIG.LIST_TTL)
      if (cachedData) {
        commit('SET_ZONES_LIST', {
          list: cachedData.list,
          total: cachedData.total
        })
        commit('SET_PAGINATION', { page: 1 })
        return cachedData
      }
    }
    
    commit('SET_LOADING', { type: 'list', loading: true })
    try {
      const response = await EcologicalZonesAPI.getZonesList(params)
      if (response.success) {
        // 缓存数据（仅缓存第一页的完整数据）
        if (reset || params.page === 1) {
          cacheUtils.set(cache.list, cacheKey, response.data)
        }
        
        if (reset) {
          commit('SET_ZONES_LIST', {
            list: response.data.list,
            total: response.data.total
          })
          commit('SET_PAGINATION', { page: 1 })
        } else {
          commit('ADD_ZONES_TO_LIST', response.data.list)
        }
        return response.data
      }
    } catch (error) {
      console.error('获取生态管控区列表失败:', error)
      
      // 如果是网络错误且有缓存数据，使用缓存数据
      if (reset) {
        const cachedData = cacheUtils.get(cache.list, cacheKey, CACHE_CONFIG.LIST_TTL * 2) // 允许使用更旧的缓存
        if (cachedData) {
          // 网络请求失败，使用缓存数据
          commit('SET_ZONES_LIST', {
            list: cachedData.list,
            total: cachedData.total
          })
          commit('SET_PAGINATION', { page: 1 })
          return cachedData
        }
      }
      throw error
    } finally {
      commit('SET_LOADING', { type: 'list', loading: false })
    }
  },
  
  // 获取管控区详情（带缓存）
  async fetchZoneDetail({ commit }, { zoneId, forceRefresh = false }) {
    // 检查缓存
    if (!forceRefresh) {
      const cachedData = cacheUtils.get(cache.detail, zoneId, CACHE_CONFIG.DETAIL_TTL)
      if (cachedData) {
        commit('SET_SELECTED_ZONE', cachedData)
        return cachedData
      }
    }
    
    commit('SET_LOADING', { type: 'detail', loading: true })
    try {
      const response = await EcologicalZonesAPI.getZoneDetail(zoneId)
      if (response.success) {
        // 缓存详情数据
        cacheUtils.set(cache.detail, zoneId, response.data)
        commit('SET_SELECTED_ZONE', response.data)
        return response.data
      }
    } catch (error) {
      console.error('获取生态管控区详情失败:', error)
      
      // 如果网络请求失败，尝试使用过期的缓存数据
      const cachedData = cacheUtils.get(cache.detail, zoneId, CACHE_CONFIG.DETAIL_TTL * 2)
      if (cachedData) {
        // 网络请求失败，使用过期缓存数据
        commit('SET_SELECTED_ZONE', cachedData)
        return cachedData
      }
      throw error
    } finally {
      commit('SET_LOADING', { type: 'detail', loading: false })
    }
  },
  
  // 空间点查询
  async spatialPointQuery({ commit }, { longitude, latitude }) {
    commit('SET_LOADING', { type: 'spatial', loading: true })
    try {
      const response = await EcologicalZonesAPI.pointQuery(longitude, latitude)
      if (response.success) {
        commit('SET_SPATIAL_QUERY_RESULTS', response.data)
        return response.data
      }
    } catch (error) {
      console.error('生态管控区点查询失败:', error)
      throw error
    } finally {
      commit('SET_LOADING', { type: 'spatial', loading: false })
    }
  },
  
  // 空间距离查询
  async spatialRadiusQuery({ commit }, { longitude, latitude, radius }) {
    commit('SET_LOADING', { type: 'spatial', loading: true })
    try {
      const response = await EcologicalZonesAPI.radiusQuery(longitude, latitude, radius)
      if (response.success) {
        commit('SET_SPATIAL_QUERY_RESULTS', response.data)
        return response.data
      }
    } catch (error) {
      console.error('生态管控区距离查询失败:', error)
      throw error
    } finally {
      commit('SET_LOADING', { type: 'spatial', loading: false })
    }
  },
  
  // 空间边界框查询
  async spatialBboxQuery({ commit }, bbox) {
    commit('SET_LOADING', { type: 'spatial', loading: true })
    try {
      const response = await EcologicalZonesAPI.bboxQuery(bbox)
      if (response.success) {
        commit('SET_SPATIAL_QUERY_RESULTS', response.data)
        return response.data
      }
    } catch (error) {
      console.error('生态管控区边界框查询失败:', error)
      throw error
    } finally {
      commit('SET_LOADING', { type: 'spatial', loading: false })
    }
  },
  
  // 获取关联应急资源
  async fetchRelatedEmergencyResources({ commit }, { zoneId, radius = 5000 }) {
    try {
      const response = await EcologicalZonesAPI.getRelatedEmergencyResources(zoneId, radius)
      if (response.success) {
        commit('SET_RELATED_EMERGENCY_RESOURCES', response.data)
        return response.data
      }
    } catch (error) {
      console.error('获取关联应急资源失败:', error)
      throw error
    }
  },
  
  // 导出数据
  async exportZonesData({ commit }, params) {
    commit('SET_LOADING', { type: 'export', loading: true })
    try {
      const response = await EcologicalZonesAPI.exportZonesData(params)
      return response
    } catch (error) {
      console.error('导出生态管控区数据失败:', error)
      throw error
    } finally {
      commit('SET_LOADING', { type: 'export', loading: false })
    }
  },
  
  // 搜索管控区
  async searchZones({ dispatch, commit }, query) {
    commit('SET_SEARCH_QUERY', query)
    commit('SET_PAGINATION', { page: 1 })
    return await dispatch('fetchZonesList', { reset: true })
  },
  
  // 应用筛选条件
  async applyFilters({ dispatch, commit }, filters) {
    commit('SET_FILTERS', filters)
    commit('SET_PAGINATION', { page: 1 })
    return await dispatch('fetchZonesList', { reset: true })
  },
  
  // 重置筛选和搜索
  async resetFiltersAndSearch({ dispatch, commit }) {
    commit('RESET_FILTERS')
    commit('SET_PAGINATION', { page: 1 })
    return await dispatch('fetchZonesList', { reset: true })
  },
  
  // 激活生态管控区模式（优化版）
  async activateEcologicalMode({ dispatch, commit }) {
    commit('SET_ECOLOGICAL_MODE', true)
    commit('SET_LAYER_VISIBLE', true)
    
    // 并行获取统计数据和列表数据，提升加载速度
    const promises = [
      dispatch('fetchStats'),
      dispatch('fetchZonesList', { reset: true })
    ]
    
    try {
      await Promise.all(promises)
    } catch (error) {
      console.error('激活生态管控区模式失败:', error)
      // 即使部分数据加载失败，也不影响模式激活
    }
  },
  
  // 预加载数据
  async preloadData({ dispatch }) {
    try {
      // 预加载统计数据和基础列表数据
      await Promise.all([
        dispatch('fetchStats'),
        dispatch('fetchZonesList', { reset: true })
      ])
    } catch (error) {
      console.warn('预加载数据失败:', error)
    }
  },
  
  // 批量获取管控区详情
  async batchFetchZoneDetails({ dispatch }, zoneIds) {
    const promises = zoneIds.map(id => 
      dispatch('fetchZoneDetail', { zoneId: id }).catch(error => {
        // 获取管控区详情失败
        return null
      })
    )
    
    const results = await Promise.all(promises)
    return results.filter(result => result !== null)
  },
  
  // 清理缓存
  clearCache({ commit }) {
    // 清理内存缓存
    cache.stats = { data: null, timestamp: 0 }
    cache.list.clear()
    cache.detail.clear()
    cache.spatial.clear()
    
    // 清理localStorage
    persistenceUtils.clear('stats')
    
    // 生态管控区缓存已清理
  },
  
  // 缓存维护（清理过期缓存）
  maintainCache() {
    cacheUtils.clearExpired(cache.list, CACHE_CONFIG.LIST_TTL)
    cacheUtils.clearExpired(cache.detail, CACHE_CONFIG.DETAIL_TTL)
    cacheUtils.clearExpired(cache.spatial, CACHE_CONFIG.LIST_TTL)
    
    // 缓存维护完成
  },
  
  // 获取缓存统计信息
  getCacheStats() {
    return {
      stats: {
        cached: !!cache.stats.data,
        age: cache.stats.timestamp ? Date.now() - cache.stats.timestamp : 0
      },
      list: {
        count: cache.list.size,
        keys: Array.from(cache.list.keys())
      },
      detail: {
        count: cache.detail.size,
        keys: Array.from(cache.detail.keys())
      },
      spatial: {
        count: cache.spatial.size,
        keys: Array.from(cache.spatial.keys())
      }
    }
  },
  
  // 关闭生态管控区模式
  closeEcologicalMode({ commit }) {
    commit('SET_ECOLOGICAL_MODE', false)
    commit('SET_LAYER_VISIBLE', false)
    commit('SET_SELECTED_ZONE', null)
    commit('SET_SPATIAL_QUERY_RESULTS', [])
  }
}

const getters = {
  // 获取统计数据
  getStats: state => state.stats,
  
  // 获取管控区列表
  getZonesList: state => state.zonesList,
  
  // 获取选中的管控区
  getSelectedZone: state => state.selectedZone,
  
  // 是否处于生态管控区模式
  isEcologicalMode: state => state.isEcologicalMode,
  
  // 获取搜索查询
  getSearchQuery: state => state.searchQuery,
  
  // 获取筛选条件
  getFilters: state => state.filters,
  
  // 获取分页信息
  getPagination: state => state.pagination,
  
  // 获取图层状态
  getLayerState: state => ({
    visible: state.layerVisible,
    opacity: state.layerOpacity
  }),
  
  // 获取空间查询结果
  getSpatialQueryResults: state => state.spatialQueryResults,
  
  // 获取关联应急资源
  getRelatedEmergencyResources: state => state.relatedEmergencyResources,
  
  // 获取加载状态
  getLoadingState: state => state.loading,
  
  // 是否有搜索或筛选条件
  hasActiveFilters: state => {
    return !!(state.searchQuery || 
             state.filters.type || 
             state.filters.level || 
             state.filters.city)
  },
  
  // 获取缓存状态
  getCacheStatus: () => ({
    stats: {
      cached: cacheUtils.isValid(cache.stats, CACHE_CONFIG.STATS_TTL),
      age: cache.stats.timestamp ? Date.now() - cache.stats.timestamp : 0
    },
    listCacheSize: cache.list.size,
    detailCacheSize: cache.detail.size,
    spatialCacheSize: cache.spatial.size
  }),
  
  // 获取性能统计
  getPerformanceStats: state => {
    const totalZones = state.zonesList.length
    const hasSelectedZone = !!state.selectedZone
    const activeFilters = !!(state.searchQuery || state.filters.type || state.filters.level || state.filters.city)
    
    return {
      totalZones,
      hasSelectedZone,
      activeFilters,
      isLoading: Object.values(state.loading).some(loading => loading),
      cacheHitRate: cache.list.size > 0 ? (cache.list.size / Math.max(1, totalZones)) * 100 : 0
    }
  },
  
  // 按类型分组的管控区列表
  getZonesByType: state => {
    const grouped = {}
    state.zonesList.forEach(zone => {
      const type = zone.type || '未分类'
      if (!grouped[type]) {
        grouped[type] = []
      }
      grouped[type].push(zone)
    })
    return grouped
  },
  
  // 按级别分组的管控区列表
  getZonesByLevel: state => {
    const grouped = {}
    state.zonesList.forEach(zone => {
      const level = zone.level || '未分级'
      if (!grouped[level]) {
        grouped[level] = []
      }
      grouped[level].push(zone)
    })
    return grouped
  },
  
  // 获取筛选后的管控区数量
  getFilteredZonesCount: state => {
    if (!state.searchQuery && !state.filters.type && !state.filters.level && !state.filters.city) {
      return state.pagination.total
    }
    return state.zonesList.length
  },
  
  // 是否可以加载更多数据
  canLoadMore: state => {
    const currentPage = state.pagination.page
    const pageSize = state.pagination.pageSize
    const total = state.pagination.total
    const loadedCount = currentPage * pageSize
    
    return loadedCount < total && !state.loading.list
  },
  
  // 获取当前查询的缓存键
  getCurrentCacheKey: state => {
    const params = {
      keyword: state.searchQuery,
      type: state.filters.type,
      level: state.filters.level,
      city: state.filters.city,
      page: state.pagination.page,
      pageSize: state.pagination.pageSize
    }
    return cacheUtils.generateKey(params)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}